// mutationQueue.ts — IndexedDB-pohjainen queue offline-mutaatioille.
//
// Idea: kun käyttäjä tekee POST/PUT/PATCH/DELETE -kutsun ilman nettiä,
// pyyntö tallennetaan IndexedDB:hen ja synkronoidaan kun yhteys palaa.
// FIFO-järjestys, max 3 retryä per item.
//
// HUOM: vain JSON-bodyt tuetaan tällä hetkellä. FormData (kuvanlataukset)
// epäonnistuu siististi — niitä ei voi serialisoida luotettavasti queue:en
// ilman raskaita base64-pakkauksia.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

export interface QueuedMutation {
  id?: number;
  url: string;
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body: string | null; // JSON-stringified body, null jos ei bodya
  headers: Record<string, string>;
  createdAt: number;
  retries: number;
  lastError?: string;
  // Käyttäjäystävällinen kuvaus banneriin (esim. "Poikkeamailmoitus", "Mittaus")
  label?: string;
}

interface MutationQueueDB extends DBSchema {
  mutations: {
    key: number;
    value: QueuedMutation;
    indexes: { 'by-createdAt': number };
  };
}

const DB_NAME = 'savroc-mutation-queue';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<MutationQueueDB>> | null = null;

function getDb(): Promise<IDBPDatabase<MutationQueueDB>> {
  if (!dbPromise) {
    dbPromise = openDB<MutationQueueDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const store = db.createObjectStore('mutations', { keyPath: 'id', autoIncrement: true });
        store.createIndex('by-createdAt', 'createdAt');
      },
    });
  }
  return dbPromise;
}

export async function enqueue(mutation: Omit<QueuedMutation, 'id' | 'createdAt' | 'retries'> & { createdAt?: number }): Promise<number> {
  const db = await getDb();
  const id = await db.add('mutations', {
    ...mutation,
    createdAt: mutation.createdAt ?? Date.now(),
    retries: 0,
  });
  notifyChange();
  return id as number;
}

export async function getAll(): Promise<QueuedMutation[]> {
  const db = await getDb();
  return db.getAllFromIndex('mutations', 'by-createdAt');
}

export async function remove(id: number): Promise<void> {
  const db = await getDb();
  await db.delete('mutations', id);
  notifyChange();
}

export async function update(id: number, patch: Partial<QueuedMutation>): Promise<void> {
  const db = await getDb();
  const existing = await db.get('mutations', id);
  if (!existing) return;
  await db.put('mutations', { ...existing, ...patch });
  notifyChange();
}

export async function count(): Promise<number> {
  const db = await getDb();
  return db.count('mutations');
}

export async function clear(): Promise<void> {
  const db = await getDb();
  await db.clear('mutations');
  notifyChange();
}

// ── Change notification ──────────────────────────────────────
// React-komponentit (esim. OfflineBanner) voivat kuunnella tätä eventtiä
// päivittääkseen counterinsa real-time-tyylisesti.

const CHANGE_EVENT = 'mutation-queue:change';

function notifyChange() {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function onQueueChange(handler: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}
